// src/utils/bookingUtils.js
//
// Shared UI helpers for the Point of Sale build.
//
// Ported from the court-booking system's bookingUtils.js, trimmed to what the
// POS screens actually call: the loading spinners and the logo cache that
// feeds them. The booking arithmetic that made up the rest of that file —
// slot availability, rate bands, reservation grouping — has no caller here and
// was deliberately left behind rather than carried over dead.
//
// The filename is kept because the project guidelines name it: every modal or
// screen that waits on data shows loadingSpinner from bookingUtils.js.

// The club logo shown in the middle of every loading ring. Cached in
// localStorage because the screens that need it render BEFORE config exists:
// AuthGuard gates on auth before any config has loaded, and the config
// spinner itself runs while `config` is still the defaults.
const LOGO_CACHE_KEY = 'jt-cached-logo-url';

/**
 * Remembers the club logo for the next boot screen. Safe to call anywhere.
 *
 * @param {string} [url] - config.logoUrl; falsy clears the cache.
 * @returns {void}
 */
export const cacheLogoUrl = (url) => {
  try {
    if (url) localStorage.setItem(LOGO_CACHE_KEY, url);
    else localStorage.removeItem(LOGO_CACHE_KEY);
  } catch {
    // Private-browsing / storage-disabled — the logo just won't show on the
    // boot screen, which is purely cosmetic. Never worth breaking boot over.
  }
};

/**
 * The logo cached by a previous session.
 *
 * @returns {string} '' on a first visit, or when storage is unreadable.
 */
export const getCachedLogoUrl = () => {
  try {
    return localStorage.getItem(LOGO_CACHE_KEY) || '';
  } catch {
    return '';
  }
};

/**
 * Just the spinner ring + label — a 12-dot rotating ring (see jt-spinner-*
 * rules in css/global.css), with no full-page wrapper. Use this to embed a
 * loading state inline, e.g. inside a modal; use loadingSpinner() below for a
 * full-page loading screen.
 *
 * The club logo renders centred inside the ring on EVERY loading state.
 * `logoUrl` is optional and only needs passing when a caller has a fresher
 * value than the cache; left out, it falls back to the cached logo, which
 * ConfigContext keeps in sync on every config load. That way new loading
 * states pick the logo up automatically instead of each caller having to
 * remember to thread it through.
 *
 * The ring itself is the element that spins and the logo is its sibling, so
 * the logo stays upright rather than rotating with the dots.
 *
 * @param {string} [message] - label under the ring; omitted renders no label.
 * @param {string} [logoUrl] - overrides the cached logo.
 * @returns {JSX.Element}
 */
export const spinnerRing = (message, logoUrl) => {
  const logo = logoUrl || getCachedLogoUrl();
  return (
    <>
      <div className="jt-spinner-wrap">
        <div className="jt-spinner">
          {Array.from({ length: 12 }).map((_, i) => (
            <span key={i} className="jt-spinner-dot" style={{ '--jt-spinner-angle': `${i * 30}deg` }} />
          ))}
        </div>
        {logo && <img src={logo} alt="" className="jt-spinner-logo" />}
      </div>
      {message && <p className="jt-spinner-label">{message}</p>}
    </>
  );
};

/**
 * Full-page loading spinner for async operations — centres spinnerRing() in a
 * full-viewport backdrop. Renders a larger ring than the inline variant (see
 * .jt-spinner-screen in css/global.css), with the logo in the middle when one
 * is available.
 *
 * @param {string} [message] - label under the ring.
 * @param {string} [logoUrl] - overrides the cached logo.
 * @returns {JSX.Element}
 */
export const loadingSpinner = (message, logoUrl) => (
  <div className="jt-spinner-screen">
    {spinnerRing(message, logoUrl)}
  </div>
);
